import { MESH_ROLES } from '../three/gltf/meshRoles';
import { DRIVER_SIDE_Z } from '../types';
import type { CarModel, CarModelWithInterior } from '../types';

/**
 * 쇼룸에 진열된 차량 카탈로그.
 *
 * 한 항목이 곧 "쇼룸의 차 한 대"입니다. 마케팅 정보(이름·가격), 3D 에셋 정보
 * (경로·스케일·회전), 실측 치수, 역할 매핑, 라이선스 표기를 한 곳에 모았습니다.
 * 쪼개 두면 같은 id로 여러 파일을 동기화해야 해서 조용히 어긋나기 쉽습니다.
 *
 * 새 차를 추가하는 순서:
 *   1. 받은 원본을 `assets/models/<폴더>/`에 두고 `bash scripts/convert-models.sh`로 압축
 *   2. `node scripts/inspect-model.mjs public/models/<이름>.glb --all` 로 값 조사
 *   3. 아래 형식대로 항목 추가 (assetIntegrity.test.ts가 이름 오타를 잡아 줍니다)
 */

// 모든 모델이 같은 디코더를 씁니다. CDN이 아니라 우리 origin에서 서빙합니다.
// (CDN에 의존하면 오프라인·차단 환경에서 뷰어가 통째로 죽습니다.)
const DRACO_DECODER_PATH = '/draco/';

// 아래에서 이 값을 쓰는 에셋은 원본 전면이 +Z입니다. +90도 돌리면 (x, y, z) -> (z, y, -x)가
// 되어 전면이 +X를 향하고, 씬 전체가 "차량 길이 = X축" 규약을 따르게 됩니다.
const FRONT_PLUS_Z_ROTATION = Math.PI / 2;

/**
 * 소렌토는 원본 전면이 -Z입니다. -90도 돌리면 (x, y, z) -> (-z, y, x)가 되어 전면이 +X를 봅니다.
 *
 * 부호를 이렇게 확정한 근거:
 *   스티어링 휠(원본 z=-0.44)과 페달(원본 z=-0.96~-1.00)이 시트(원본 z=+0.03)보다
 *   -Z 쪽에 있습니다. 운전자는 차 앞쪽에 앉으므로 -Z가 전면입니다.
 *   `template_chromered`(적색 크롬 = 후미등)가 +Z에 몰려 있는 것도 같은 결론입니다.
 *   회전 후 리어 오버행(1.07m)이 프런트(0.91m)보다 긴 것이 실차 소렌토와 일치합니다.
 */
const FRONT_MINUS_Z_ROTATION = -Math.PI / 2;

/*
 * 운전석 좌우는 모델별 속성입니다(types.ts의 DriverSide 참고).
 * 전면이 +X일 때 차량의 오른쪽이 +Z이므로, 좌핸들은 눈이 -Z, 우핸들은 +Z입니다.
 *
 * 지금 카탈로그의 실내 에셋은 전부 좌핸들이라 아래 값은 모두 DRIVER_SIDE_Z.left입니다.
 * 그래도 상수 하나로 접지 않고 모델별로 두는 이유는, 우핸들 에셋이 실제로 있었고
 * (클래식 미니, 지금은 카탈로그에서 제거됨) 부호를 잘못 넣으면 카메라가 조수석에
 * 앉는데 그건 렌더를 봐야만 드러나기 때문입니다. 선언해 두면 테스트가 대조합니다.
 */

export const CAR_MODELS: CarModel[] = [
  {
    id: 'taycan-turbo-s',
    brand: 'Porsche',
    name: 'Porsche Taycan',
    category: '전기 스포츠 세단',
    powertrain: 'ev',
    shortName: 'Taycan',
    // 2019년 9월 Zuffenhausen 공장에서 생산 시작. 2024년 부분변경도 같은 J1 세대이고
    // 아직 생산 중입니다(2030년 단산 보도가 있지만 확정된 종료가 아닙니다).
    productionYears: { start: 2019 },
    basePrice: 232600000,
    // 원본 에셋의 Turbo S 사양. Porsche 공개 최고속도 260km/h, Launch Control 0→100 2.8초.
    topSpeedKmh: 260,
    zeroTo100KmhSeconds: 2.8,
    // Edmunds 2023 Taycan Turbo S 공차중량 5,101 lb.
    kerbWeightKg: 2315,
    // Performance Battery Plus: 83.7kWh usable, Porsche 공개 최대 회생 265kW.
    electricEnergy: {
      usableBatteryKWh: 83.7,
      initialSoc: 0.8,
      regenLimitKw: 265,
      driveEfficiency: 0.9,
      regenEfficiency: 0.78,
      referenceConsumptionWhPerKm: 230,
      accessoryPowerKw: 0.8,
    },

    asset: {
      url: '/models/taycan.glb',
      dracoDecoderPath: DRACO_DECODER_PATH,
      // 원본 전면 방향(모델 로컬 좌표). 헤드라이트 z=+1.963, 테일라이트 z=-2.073.
      sourceFrontAxis: [0, 0, 1],
      rotationY: FRONT_PLUS_Z_ROTATION,
      scale: 1,
      position: [0, 0, 0],
    },

    // 실측 치수(회전·스케일·이동을 모두 적용한 뒤의 월드 좌표).
    // 실물 스펙 대비: 길이 4.963 / 높이 1.395 / 휠베이스 2.900
    bounds: {
      rearX: -2.475,
      frontX: 2.475,
      groundY: 0,
      roofY: 1.393,
      halfWidth: 1.07,
      frontWheelX: 1.489,
      rearWheelX: -1.395,
      wheelHalfTrack: 0.837,
      tireOuterRadius: 0.358,
    },

    /**
     * 운전석 눈높이. 스티어링 휠(Object_21) 중심 [0.396, 0.868, -0.37]에서
     * 뒤로 0.41m, 위로 0.17m 물러난 지점입니다. 헤드리스 렌더로 프레이밍을 확인했습니다.
     */
    interiorEye: [-0.014, 1.038, 0.37 * DRIVER_SIDE_Z.left],
    driverSide: 'left',

    /**
     * 이 에셋은 화면을 별개 면으로 모델링하지 않았습니다.
     * 실내 머티리얼 전체를 연결 요소로 훑어도(121개) 운전석 앞 디스플레이 위치에는
     * 아무 판도 없고, 도어 스위치 패널·리어 스피커 그릴만 나옵니다.
     * 대시보드를 측정해 보면 x 0.54~0.95 / y 0.93~1.0 구간이 법선이 위를 향한
     * 가로 선반이라, 텍스처를 입힐 수직면 자체가 없습니다.
     *
     * 그래서 판을 하나 만들어 붙입니다. 붙일 자리는 대시보드 위가 아니라
     * 센터 스택 전면부입니다. 대시보드 위는 법선이 하늘을 향한 가로 선반이라
     * 판을 세우면 받쳐 주는 게 없어서 공중에 뜬 것처럼 보입니다(실제로 그랬습니다).
     *
     * 센터 스택 전면부는 실측으로 확인한 평면입니다. 법선 방향으로 걸러서 재면
     * 삼각형 53개 / 면적 0.069m² / 법선 일관성 0.94가 나옵니다.
     *   면 범위  x 0.539~0.62 / y 0.704~0.928 / z -0.044~0.368
     *   법선     [-0.985, 0.174, 0]  (운전자 쪽, 위로 10도)
     *   측정 명령 scripts/probe-dash-surface.mjs --box=0.30,0.75,0.70,1.05,-0.05,0.40
     *            --normal=-1,0.25,0 --normal-min=0.85
     *
     * 공조구가 이 면의 아래쪽을 쓰므로 판은 위쪽 띠에 얹습니다.
     * 그 띠만 따로 재면 y 0.825~0.928(높이 0.10) / z -0.091~0.428(폭 0.52)이고
     * 일관성 0.929입니다. 실차 사진과 같은 "가로로 누운 밴드" 자리입니다.
     * 면을 파고들지 않게 법선 방향으로 5mm 띄웠습니다(z-fighting 방지).
     *
     * 좌표는 앱 좌표계(rotationY·scale 적용 후) 기준입니다.
     */
    displayPanel: {
      position: [0.574, 0.876, 0.16],
      /*
       * 가로 x 세로 (m). 실차 Taycan의 센터 디스플레이는 대시보드에 넓게 누운
       * 가로 밴드라서 3:1 정도로 잡습니다. 정사각형에 가깝게 만들면 실차와 다르고,
       * 붙일 띠의 높이(0.10)를 넘겨서 위아래가 면 밖으로 나갑니다.
       */
      size: [0.28, 0.088],
      /*
       * 판이 향할 방향. 운전석 눈을 보게 하는 것보다 면의 실측 법선에 맞추는 편이
       * 정확합니다. 눈을 보게 하면 각도가 몇 도 어긋나서 판의 한쪽 변이 면을
       * 파고들거나 들립니다.
       */
      normal: [-0.985, 0.174, 0.001],
      backgroundColor: '#0a0d14',
    },

    /**
     * 역할별 메시 선택자.
     *
     * 이 에셋은 Sketchfab을 거치며 노드 이름이 `Object_53` 같은 자동 생성 이름으로
     * 바뀌었지만, 머티리얼 이름은 원저자가 붙인 포르투갈어가 그대로 남아 있습니다.
     *   Pintura = 도장, Vidros = 유리, Interno = 실내
     *
     * 림만 메시 이름으로 고릅니다. 림이 쓰는 `Carro_Metal_Preto`를 차체 부품 8개가
     * 함께 쓰기 때문에, 머티리얼로 고르면 휠 옵션이 차체까지 칠해 버립니다.
     */
    selectors: {
      [MESH_ROLES.PAINT]: { materials: ['Carro_Pintura'] },
      [MESH_ROLES.GLASS]: { materials: ['Carro_Vidros'] },
      [MESH_ROLES.RIM]: { meshes: ['Object_42', 'Object_43', 'Object_44', 'Object_45'] },
      [MESH_ROLES.SEAT]: { materials: ['Carro_Interno_1'] },
      [MESH_ROLES.TRIM]: { materials: ['Carro_Interno'] },
      /**
       * 스티어링 휠 센터 패드. 크레스트가 부조로 새겨진 부품입니다
       * (삼각형 1,846개 / 연결 요소 1개 / 크기 6.8 x 12.9 x 13.6 cm).
       *
       * 이 에셋에는 크레스트용 색도 텍스처도 없습니다. 텍스처는 헤드·테일램프
       * 리플렉터 2개뿐이고, `Carro_logo__Gold_1`은 로고가 아니라 타이어 사이드월
       * 각인(삼각형 2개짜리 평면 15개, z ±0.96 / y 0.35)입니다.
       *
       * 외장은 배지 "자리"만 형상으로 새겨져 있고 배지 자체는 없습니다.
       * 노즈 중앙에 약 2mm 오목한 방패 윤곽이 있습니다(아래 hood-crest 참고).
       *
       * 그래서 색을 입히는 게 아니라 광택을 줘서 부조를 드러냅니다.
       * 크레스트 색은 아래 decals가 별도 이미지로 얹습니다.
       * 메시 이름 지정이 머티리얼 지정보다 우선하므로 TRIM에서 이 부품만 빠집니다.
       */
      [MESH_ROLES.EMBLEM]: { meshes: ['Object_20'] },
    },

    /**
     * 표면에 덧붙이는 이미지 데칼.
     *
     * 이 에셋에는 포르쉐 크레스트가 색으로도 텍스처로도 없습니다. 그래서 같은
     * 프로젝트의 718 Boxster 에셋(Ddiaz Design, CC-BY-4.0) 배지 아틀라스에서
     * 크레스트를 잘라내 씁니다. 자세한 출처와 상표 관련 주의는
     * three/materials/decalImages.ts 주석을 참고하세요.
     *
     * 붙일 면은 스티어링 센터 패드의 운전자 방향면입니다. 실측하면
     *   법선     [-0.945, 0.326, 0]  (뒤로 19도 기울어진 평면, 일관성 0.989)
     *   평균면 중심 [0.3687, 0.8595, -0.370]
     *
     * 이 면에는 얕은 부조가 있어서 평균면에 가깝게 붙이면 부조에 가려집니다.
     * 처음에 2mm만 띄웠더니 크레스트가 부조 뒤로 숨어 테두리만 보였습니다.
     * 정점 2,166개의 법선 방향 부호거리를 재면
     *   중앙값 +0.88mm / 95% +2.52mm / 최대 +2.64mm
     * 그래서 최대 돌출보다 앞인 3.2mm(면 기준점에서 23.87mm)를 띄웁니다.
     *
     * 좌우/상하 위치는 원판의 기하 중심에 맞춥니다.
     *   센터 패드는 `Object_20` 단일 부품이고, 정점을 패드 평면에 투영하면
     *   범위가 u(폭) -0.068~0.068 / v(위) -0.066~0.071 입니다.
     *   따라서 중심은 u 0.000 / v +0.0025.
     *
     *   처음에는 "돌출 상위 10% 발자리 중심"(y 0.8549 / z -0.3699)에 맞췄습니다.
     *   그 발자리는 원판 중심보다 6.6mm 아래여서 크레스트가 원판 안에서 내려앉아
     *   보였습니다. 부조 발자리는 크레스트 윤곽이 아니라 "가장 튀어나온 부분"의
     *   범위라 중심 기준으로 쓸 수 없습니다. 부품 정점 범위로 잡아야 합니다.
     *
     * 종횡비는 추출한 이미지(186:262 = 0.710)를 지켜야 크레스트가 찌그러지지
     * 않습니다. 높이 38mm에 맞춰 폭 27mm입니다.
     */
    decals: [
      {
        id: 'steering-crest',
        image: 'porscheCrest',
        /*
         * 원판 기하 중심(패드 평면 좌표 u 0, v +0.0025)을 법선 방향으로
         * 23.87mm 띄운 좌표입니다. z가 정확히 원판 중심(-0.37)입니다.
         */
        position: [0.3663, 0.8621, -0.37],
        normal: [-0.945, 0.326, 0],
        size: [0.027, 0.038],
        // 에나멜 배지라 살짝 광택이 있습니다.
        metalness: 0.4,
        roughness: 0.3,
      },

      /**
       * 보닛(프렁크 리드) 크레스트.
       *
       * 이 에셋은 외장 배지가 지오메트리로도 텍스처로도 없어서 보닛이 비어
       * 있었습니다.
       *
       * 이 에셋에는 배지 "자리"가 형상으로 새겨져 있습니다. 매끈한 보닛 면은
       * 삼각형 2개짜리 거대한 사각형인데, 노즈 중앙에만 작은 삼각형이 빽빽한
       * 구간이 있습니다. 그 구간을 기준 평면에 투영해 깊이를 격자로 찍으면
       * 방패 윤곽이 나옵니다.
       *   깊이     주변 패널보다 약 2mm 오목
       *   폭       57mm (z ±0.0287)
       *   높이     72mm, 아래로 갈수록 좁아져 꼭지점이 됨
       *   중심     [2.2440, 0.6799, 0]  (오목한 바닥면 기준)
       *   국소 법선 [0.462, 0.887, 0]   수직에서 27.5도
       *
       * 처음에는 이 자리를 못 찾아 보닛 상면(x 1.71)에 붙였고, 그다음엔 앞쪽
       * 경사면(x 2.085)으로 옮겼습니다. 둘 다 배지 자리보다 위였습니다.
       * 지금은 새겨진 자리에 맞춰 넣습니다.
       *
       * 크기는 자리 안에 들어가도록 높이 74mm로 잡고, 추출 이미지 종횡비
       * 0.710(186:262)을 지켜 폭 52.5mm입니다. 폭에 맞추면(57mm) 높이가 80mm가
       * 되어 자리 위아래로 넘칩니다.
       *
       * 띄우는 양:
       *   오목한 바닥이 아니라 주변 패널면을 기준으로 1.5mm 띄웠습니다.
       *   실차 배지도 자리에 얹혀 패널면보다 살짝 솟아 있습니다. 바닥에 맞추면
       *   2mm 파묻혀 테두리만 보입니다.
       *
       * 법선이 수직에서 27.5도라 lookAt만으로도 롤이 정해지긴 하지만, 세로에
       * 가까운 축에서는 결과가 불안정합니다. up으로 이미지 위쪽을 차량 뒤쪽(-X)으로
       * 못 박습니다. 차 앞에 서서 보닛을 볼 때 윈드실드 쪽이 시야의 위입니다.
       */
      {
        id: 'hood-crest',
        image: 'porscheCrest',
        position: [2.246, 0.6837, 0],
        normal: [0.462, 0.887, 0],
        up: [-1, 0, 0],
        size: [0.0525, 0.074],
        /*
         * 거의 확산면으로 둡니다.
         *
         * 처음에는 "외장 배지는 크롬이라 광택이 세다"고 보고 metalness 0.55 /
         * roughness 0.25 / envMapIntensity 1.4(기본값)를 줬습니다. 정면에서 보면
         * 보닛에 천장 조명(dark-studio 프리셋의 intensity 6 rect)이 통째로 반사돼
         * 크레스트까지 흰 덩어리로 뭉개졌습니다. metalness가 높으면 베이스 색이
         * 확산광 대신 반사 색으로만 쓰여서 텍스처가 더 쉽게 날아갑니다.
         *
         * 같은 정면 프레임에서 크레스트 영역(22x18px)을 재서 고른 값입니다.
         *   m 0.55 / r 0.25 / env 1.4  -> 평균 채도 0.141 / 포화 픽셀 16.7%
         *   m 0.15 / r 0.50 / env 0.55 -> 평균 채도 0.188 / 포화 픽셀  0.8%
         *   m 0.05 / r 0.60 / env 0.35 -> 평균 채도 0.213 / 포화 픽셀  0.0%
         * (텍스처 원본 금색 #D4B483의 채도는 0.382)
         *
         * 남은 채도 손실은 조명이 센 자리라 어쩔 수 없습니다. 주변 보닛도 같이
         * 밝게 뜨는 구간이고, 다른 각도에서는 제대로 금색으로 보입니다.
         *
         * 실내 배지는 빛이 약해서 기본값(env 1.4)이 맞습니다. 그래서 값을
         * 데칼별로 둡니다.
         */
        metalness: 0.05,
        roughness: 0.6,
        envMapIntensity: 0.35,
      },
    ],

    /**
     * 프런트 번호판.
     *
     * 왜 역할(selectors.plate)이 아니라 판을 얹는가:
     *   이 에셋에는 번호판 자리가 형상으로 분명히 있습니다. 그런데 그 면이 속한
     *   머티리얼 `Carro_Plastico`는 차 전체(삼각형 9,432개 / x -2.04~2.47)를,
     *   메시 `Object_70`도 차 전체(2,606개 / x -1.09~2.47)를 덮습니다. 둘 중
     *   무엇으로 골라도 번호판이 아니라 차 곳곳의 플라스틱 트림이 함께 물듭니다.
     *   그래서 다른 네 차처럼 원본 면에 텍스처를 입히는 방법을 쓸 수 없고,
     *   자리 위에 판을 하나 덧붙입니다.
     *
     *   처음에 이 차를 "번호판 없음"으로 분류했던 이유도 여기에 있습니다. 이름으로도
     *   (머티리얼 24개 중 번호판 관련 없음), 프리미티브 바운딩 박스로도(자리가 거대한
     *   병합 메시의 일부라 판 모양으로 잡히지 않음) 드러나지 않습니다. 상자로 잘라
     *   법선을 재야 나옵니다.
     *
     * 자리 실측 (법선이 +X인 삼각형 40개만 걸러서, 정점 기준):
     *   범위      x 2.4603~2.4717 / y 0.4091~0.5164 / z -0.2569~0.2569
     *   크기      513.9 x 107.3 mm  (유럽 번호판 520 x 110 mm와 거의 같습니다)
     *   중심      [2.4660, 0.4628, 0]
     *   평균 법선 [1, -0.0092, 0]  (정면에서 아래로 0.53도)
     *   일관성    평균 내적 0.998 / 최악 0.9962 — 사실상 완전한 평면입니다
     *   측정 명령 probe-dash-surface.mjs 계열로 상자 자르고 법선 필터
     *            --box=2.40,2.48,0.35,0.60,-0.35,0.35 --normal=1,0,0 --normal-min=0.9
     *   교차 확인 probe-visible-surface.mjs --views=front --by=mesh
     *            --highlight=Object_70 로 정면 렌더를 찍으면, 앞범퍼 아래
     *            가운데에 번호판 모양 사각형 하나만 칠해집니다.
     *
     * 크기는 자리 실측값을 그대로 씁니다(513 x 107 mm). 비율 4.79는 자리의 4.789와
     * 0.1% 안에서 같습니다.
     *
     * 띄우는 양:
     *   자리의 가장 앞선 정점이 x 2.4717입니다. 판 중심을 2.4742에 두면 판이
     *   기울어져 있어도(위아래로 ±0.5mm) 어디서나 최소 1.8mm 앞에 있습니다.
     *   실내 워드마크 데칼이 1.54mm로 z-fighting 없이 동작하므로 충분합니다.
     *   차량 전체 앞끝(bounds.frontX 2.475)보다 안쪽이라 카메라 프레이밍에도
     *   영향이 없습니다.
     *
     * 법선이 거의 수평이라 up을 따로 주지 않아도 lookAt이 롤을 안정적으로 정합니다
     * (보닛 크레스트는 법선이 수직에 가까워서 up이 필요했습니다).
     *
     * 재질은 다른 네 차의 번호판(createPlateMaterials)과 같은 값으로 맞춥니다.
     * SurfaceDecal 기본값(m 0.35 / r 0.35 / env 1.4)은 배지용이라 번호판에는
     * 금속기가 과합니다.
     */
    platePanel: {
      position: [2.4742, 0.4628, 0],
      normal: [1, -0.0092, 0],
      size: [0.513, 0.107],
      metalness: 0.05,
      roughness: 0.45,
      envMapIntensity: 0.9,
    },

    credit: {
      title: '2023 Porsche Taycan Turbo S',
      author: 'tonielpro520',
      authorUrl: 'https://sketchfab.com/tonielpro520',
      modelUrl: 'https://sketchfab.com/3d-models/2023-porsche-taycan-turbo-s-862b651f6dc644b9a4da47990880ae28',
      licenseName: 'CC BY 4.0',
      licenseUrl: 'http://creativecommons.org/licenses/by/4.0/',
      allowsCommercialUse: true,
    },
  },

  {
    id: 'boxster-718-s',
    brand: 'Porsche',
    name: 'Porsche 718 Boxster',
    category: '미드십 로드스터',
    powertrain: 'ice',
    shortName: '718 Boxster',
    /*
     * 982 세대. "718" 이름을 쓴 기간(2016~2025)과 정확히 겹칩니다.
     * 내연기관 718 생산은 2025년 10월에 끝났고 후속은 전기차입니다.
     */
    productionYears: { start: 2016, end: 2025 },
    basePrice: 98600000,
    // 원본 에셋의 718 Boxster S + Sport Chrono 사양.
    topSpeedKmh: 285,
    zeroTo100KmhSeconds: 4.2,
    // Edmunds 2017 718 Boxster S 공차중량 2,988 lb (PDK).
    kerbWeightKg: 1355,

    asset: {
      url: '/models/boxster.glb',
      dracoDecoderPath: DRACO_DECODER_PATH,
      // 그릴이 z=+0.02, 번호판·테일라이트가 z=-0.021 쪽입니다.
      sourceFrontAxis: [0, 0, 1],
      rotationY: FRONT_PLUS_Z_ROTATION,
      // 원본이 전장 0.044 단위로 저장돼 있습니다. 100배 하면 4.4m가 되어
      // 실물 718 Boxster(4.379m)와 맞습니다. 폭 2.0m, 높이 1.2m도 실물과 일치합니다.
      scale: 100,
      position: [0, 0, 0],
    },

    bounds: {
      rearX: -2.2,
      frontX: 2.2,
      groundY: 0,
      roofY: 1.2,
      halfWidth: 1.0,
      frontWheelX: 1.2,
      rearWheelX: -1.3,
      wheelHalfTrack: 0.7,
      tireOuterRadius: 0.35,
    },

    /**
     * 이 에셋은 실내가 통짜 메시 하나여서 스티어링 휠을 따로 짚을 수 없습니다.
     * 그래서 실내 유리(x=-0.6, y=0.9)와 시트벨트 앵커(x=-0.4) 위치를 기준으로
     * 그보다 앞·위에 눈높이를 잡았습니다. 로드스터라 눈높이가 낮습니다.
     */
    interiorEye: [-0.16, 0.95, 0.37 * DRIVER_SIDE_Z.left],
    driverSide: 'left',

    /**
     * 머티리얼 이름이 그대로 역할을 알려주는 드문 경우입니다(carpaint / glass / interior).
     *
     * 림만 메시 이름으로 고릅니다. `wheel` 머티리얼 하나를 타이어·브레이크·림이
     * 모두 공유(프리미티브 92개)하기 때문입니다. 실측으로 지름을 재서 갈랐습니다.
     *   지름 0.52~0.56m 디스크 = 림  /  지름 0.66~0.69m = 타이어(제외)
     *
     * `_1` 접미사가 붙은 이름이 왜 필요한가:
     *   이 glTF는 좌우 뒷바퀴 노드에 똑같은 이름(`polySurface173_wheel_0`)을 씁니다.
     *   three의 GLTFLoader는 이름이 겹치면 두 번째부터 `_1`, `_2`를 붙여 개명합니다.
     *   그래서 원래 이름만 적으면 한쪽 바퀴만 잡혀서, 카메라가 보는 쪽이 어느 쪽이냐에
     *   따라 "앞바퀴만 색이 바뀌는" 증상이 됩니다. 실제로 그렇게 렌더됐습니다.
     *   assetIntegrity.test.ts가 이 중복을 찾아 누락된 변형을 잡아 줍니다.
     */
    selectors: {
      [MESH_ROLES.PAINT]: { materials: ['carpaint'] },
      [MESH_ROLES.GLASS]: { materials: ['glass'] },
      [MESH_ROLES.RIM]: {
        meshes: [
          // 앞바퀴 (좌우 노드 이름이 서로 달라서 그대로 씁니다)
          'polySurface220_wheel_0',
          'polySurface210_wheel_0',
          'polySurface236_wheel_0',
          'polySurface196_wheel_0',
          // 뒷바퀴 (좌우가 같은 이름 -> 두 번째는 three가 `_1`로 개명)
          'polySurface173_wheel_0',
          'polySurface173_wheel_0_1',
          'polySurface189_wheel_0',
          'polySurface189_wheel_0_1',
        ],
      },
      /**
       * 원본은 실내 전체가 머티리얼 하나(`interior`)였습니다. 그대로 쓰면 내장
       * 색상을 고를 때 시트부터 대시보드·스티어링까지 한 색으로 칠해졌습니다.
       *
       * 그래서 빌드 단계에서 실내 메시를 부품 단위로 갈랐습니다
       * (scripts/split-interior.mjs). 시트와 대시보드는 정점을 공유하지 않는
       * 별개 물체라서, 정점을 공유하는 삼각형끼리 묶으면(연결 요소) 부품 경계가
       * 그대로 되살아납니다. 그 중 좌우로 벌어져 있고 스티어링보다 뒤에 있으며
       * 도어 패널보다 안쪽인 부품 10개(삼각형 5,614개)를 시트로 떼어냈습니다.
       *   시트: 등받이 좌우 1,728개씩 / 쿠션 좌우 674개씩 / 헤드레스트 356개씩 등
       *   트림: 대시보드·터널·격벽 통합 셸 13,570개, 스티어링 휠 4,378개,
       *         도어 패널 좌우 2,649개씩 등 330개 부품
       */
      [MESH_ROLES.SEAT]: { materials: ['interior_seat'] },
      [MESH_ROLES.TRIM]: { materials: ['interior'] },
      /**
       * 리어 번호판. 머티리얼 `plate`를 프리미티브 하나만 쓰기 때문에
       * (`Boxster_S:ManufacturerPlate_plate_0`) 머티리얼로 골라도 다른 부위로 새지 않습니다.
       *
       * 원본은 독일식 딜러 번호판인데 baseColor 말고 emissive까지 달려 있습니다
       * (emissiveFactor [1,1,1] + textures/plate_emissive.png). 즉 판이 스스로 빛납니다.
       * 우리 판으로 갈아끼우면 그 발광도 사라지는데, 실물 번호판은 빛나지 않으므로
       * 그쪽이 맞습니다.
       */
      [MESH_ROLES.PLATE]: { materials: ['plate'] },
    },

    /**
     * 번호판 가시면 실측: z 0.305 x y 0.147 m (305 x 147 mm, 미국식 판 크기).
     *
     * 전체 바운딩 박스가 아니라 법선이 -X를 향한 삼각형 32개만 걸러서 쟀습니다.
     * 이 판은 테두리가 있어서 전체로 재면 두께 47mm가 섞여 비율이 틀립니다.
     * UV는 u 0.016~0.984 / v 0.031~0.969로 0~1을 거의 다 써서, 텍스처를 그대로
     * 갈아끼우면 판에 꽉 찹니다(평면 맞춤 잔차 0.03%).
     */
    plate: { aspect: 2.07 },

    credit: {
      title: '2017 Porsche 718 Boxster S',
      author: 'Ddiaz Design',
      authorUrl: 'https://sketchfab.com/ddiaz-design',
      modelUrl: 'https://sketchfab.com/3d-models/2017-porsche-718-boxster-s-4b52cfc4ce32438aa732880fae81c047',
      licenseName: 'CC BY 4.0',
      licenseUrl: 'http://creativecommons.org/licenses/by/4.0/',
      allowsCommercialUse: true,
    },
  },

  {
    id: 'tesla-model-3',
    brand: 'Tesla',
    name: 'Tesla Model 3',
    category: '전기 세단',
    powertrain: 'ev',
    shortName: 'Model 3',
    // 2017년 7월 첫 인도. 2023년 Highland 부분변경도 같은 세대이고 아직 생산 중입니다.
    productionYears: { start: 2017 },
    basePrice: 54990000,
    // 2024 에셋에 세부 트림 표기가 없어 일반 RWD 사양을 사용합니다.
    topSpeedKmh: 200,
    zeroTo100KmhSeconds: 6.2,
    // Edmunds 2024 Model 3 공차중량 3,891 lb.
    kerbWeightKg: 1765,
    // RWD LFP 팩의 대표 usable 용량과 복합 실주행 전비.
    electricEnergy: {
      usableBatteryKWh: 57.5,
      initialSoc: 0.8,
      regenLimitKw: 75,
      driveEfficiency: 0.92,
      regenEfficiency: 0.76,
      referenceConsumptionWhPerKm: 145,
      accessoryPowerKw: 0.65,
    },

    asset: {
      url: '/models/model3.glb',
      dracoDecoderPath: DRACO_DECODER_PATH,
      // 헤드라이트 글로우가 z=+1.968, 테일라이트가 z=-2.134입니다.
      sourceFrontAxis: [0, 0, 1],
      rotationY: FRONT_PLUS_Z_ROTATION,
      scale: 1,
      // 원본 최저점이 y=-0.001이라 1mm 가라앉습니다. 눈에 보이진 않지만 맞춰 둡니다.
      position: [0, 0.001, 0],
    },

    // 실물 스펙 대비: 길이 4.720 / 높이 1.441 / 휠베이스 2.875
    bounds: {
      rearX: -2.378,
      frontX: 2.341,
      groundY: 0,
      roofY: 1.443,
      halfWidth: 1.044,
      frontWheelX: 1.463,
      rearWheelX: -1.411,
      wheelHalfTrack: 0.807,
      tireOuterRadius: 0.333,
    },

    // 스티어링 휠 스포크(Object_5, [0.39, 0.898, -0.375])를 기준으로 잡았습니다.
    interiorEye: [-0.02, 1.05, 0.375 * DRIVER_SIDE_Z.left],
    driverSide: 'left',


    /**
     * 이 에셋은 머티리얼 이름이 가장 친절합니다(ext_/int_ 접두사).
     *
     * 림만 메시 이름으로 고릅니다. `RIM_2` 머티리얼을 휠(Object_25)과 실내
     * 부품(Object_52)이 함께 쓰기 때문입니다. y중심 0.333 / 지름 0.534가 휠입니다.
     *
     * 이름에 `_FL` 같은 꼬리가 붙은 이유: 이 에셋만 네 바퀴가 한 프리미티브에 병합돼
     * 와서 주행 모드에서 바퀴가 구르지 않았습니다. 빌드 단계(scripts/split-wheels.mjs)에서
     * 모서리별로 갈랐고, 지오메트리가 `Object_25` 아래 새 노드 넷으로 옮겨갔습니다.
     * 원본 노드는 껍데기(메시 없는 노드)로 남아서 이름으로 잡히지 않습니다.
     */
    selectors: {
      /**
       * 차체 내측 패널(int_carpaint)까지 함께 칠합니다.
       *
       * 왜 필요한가: 이 에셋의 도장 색은 `ext_carpaint_baseColor.png` 단색 텍스처
       * (103바이트, #b20000)로 들어 있습니다. 원본이 빨간 Model 3이었기 때문입니다.
       * `int_carpaint`가 같은 텍스처를 쓰는데 이걸 빼 두면, 우리 페인트로 갈아끼운
       * 외판 사이의 패널 틈마다 원본 빨강이 실선으로 드러납니다. 보닛 윤곽과 도어
       * 이음선이 빨간 줄로 보이던 증상이 이것이었습니다.
       *
       * 실차에서도 도어 자브와 보닛 안쪽은 차체색이라, 함께 칠하는 것이 맞습니다.
       */
      [MESH_ROLES.PAINT]: { materials: ['ext_carpaint', 'int_carpaint'] },
      // 유리는 안쪽 껍데기(INT_Window)까지 함께 잡습니다. 바깥쪽만 투명하게 만들면
      // 운전석 시점에서 안쪽 껍데기가 밝은 회색 판으로 남아 앞이 안 보입니다.
      [MESH_ROLES.GLASS]: { materials: ['EXT_Window', 'INT_Window'] },
      [MESH_ROLES.RIM]: {
        meshes: [
          'Object_25_FL',
          'Object_25_FR',
          'Object_25_RL',
          'Object_25_RR',
          'Object_28_FL',
          'Object_28_FR',
          'Object_28_RL',
          'Object_28_RR',
        ],
      },
      [MESH_ROLES.SEAT]: { materials: ['int_leather_1', 'int_leather_2', 'int_leather_3'] },
      [MESH_ROLES.TRIM]: { materials: ['INT_Plastic_Detail', 'int_headliner', 'int_carpet'] },
      /**
       * 이 에셋은 디스플레이가 독립 머티리얼로 분리돼 있어서 판을 얹지 않고
       * 원본 화면에 텍스처를 입힙니다(Taycan은 그 면이 없어서 displayPanel을 씁니다).
       * 화면 두 개가 이 머티리얼을 공유합니다(연결 요소로 확인).
       *   센터 터치스크린 0.34 x 0.22 m (1.55:1), 중심 [0.53, 0.89, 0]
       *   뒷좌석 스크린   0.19 x 0.11 m (1.70:1), 중심 [-0.24, 0.63, 0]
       */
      [MESH_ROLES.SCREEN]: { materials: ['INT_Screen'] },
      /**
       * 리어 번호판. `EXT_PLATE`를 프리미티브 하나(`Object_3`)만 씁니다.
       * 이 에셋은 앞 번호판 형상이 없어서 뒤만 바뀝니다(실차도 유럽·미국 일부를
       * 빼면 앞 번호판이 없는 시장이 있어 이상하지 않습니다).
       */
      [MESH_ROLES.PLATE]: { materials: ['EXT_PLATE'] },
    },

    /**
     * 실내 디스플레이에 띄울 화면 설정.
     *
     * aspect를 16:10으로 잡은 이유: 두 화면(1.55:1, 1.70:1)이 머티리얼을 공유해서
     * 비율을 하나만 고를 수 있습니다. 둘의 중간값이라 어느 쪽도 눈에 띄게
     * 늘어나지 않습니다.
     */
    display: {
      aspect: 16 / 10,
      // 켜져 있는 화면처럼 보이도록 완전한 검정보다 살짝 밝은 남색을 씁니다.
      backgroundColor: '#0a0d14',
    },

    /**
     * 번호판 가시면 실측: z 0.501 x y 0.110 m (501 x 110 mm, 유럽식 긴 판).
     * 법선이 -X 쪽인 삼각형 108개로 쟀습니다.
     *
     * 이 판의 UV는 v가 0~1이 아니라 **4~5**입니다. 정확히 한 타일 몫이 4만큼
     * 밀려 있어서, 반복 래핑이면 그대로 0~1처럼 읽힙니다. licensePlate.ts가
     * wrapT를 RepeatWrapping으로 두는 이유가 이 모델입니다.
     *
     * 판이 범퍼를 따라 휘어 있습니다(가운데가 13mm 더 들어감). UV가 그 곡률을
     * 보정해 두어서(양 끝에서 v 폭이 좁아짐) 평평한 텍스처를 얹어도 로고가
     * 고르게 펴집니다.
     *
     * 정점 100개 중 절반쯤은 u 0.0028 / v 4.9789 한 점에 몰려 있습니다. 판의
     * 측벽과 뒷면을 텍셀 하나로 접어 둔 것입니다. 그 점은 캔버스 좌상단이라
     * 판 바닥색을 집어 오므로 테두리가 판과 같은 색으로 나옵니다.
     */
    plate: { aspect: 4.57 },

    /**
     * 이 에셋에만 있는 유리 파손 표현용 오버레이 레이어입니다.
     * transparent=false / roughness=1 / normalMap 있는 불투명 머티리얼이라
     * 윈드실드 위에 얼룩덜룩한 판으로 덮여서 운전석에서 앞이 보이지 않았습니다.
     * 가려 보는 실험으로 원인을 특정했습니다(윈드실드 영역 밝기 56.3 -> 28.2).
     * 온전한 차에는 없어야 하는 레이어이므로 투명화가 아니라 숨김으로 처리합니다.
     */
    hidden: { materials: ['DAMAGE_GLASS'] },

    /**
     * 스티어링 허브 TESLA 워드마크.
     *
     * 이 에셋은 워드마크를 양각 형상으로 새겨 뒀습니다(`Object_48`, 작은 삼각형
     * 145개). 형상 자체는 온전합니다 — 원본 scene.gltf를 무손실로 변환해
     * 렌더해도 결과가 픽셀 단위로 같고, Draco 왕복 후에도 정점 253개와 부조
     * 분포(최대 +0.94mm)가 그대로입니다.
     *
     * 문제는 읽히지 않는다는 것입니다.
     *   크기      50.2 x 6.1 mm (z -0.3927~-0.3425 / y 0.8978~0.9036)
     *   부조      -1.41 ~ +0.94 mm
     *   머티리얼  int_plastic, metalness 0, roughness 0.937 (거의 완전 무광)
     * 무광 검정 표면에서 1mm 단차는 확산광만 받아 명암 차가 거의 없습니다.
     * 게다가 실내 카메라에서 워드마크는 가로 80px 남짓이라 획이 1px 이하입니다.
     * 그래서 글자가 조각조각 무너져 보입니다.
     *
     * 머티리얼을 광택으로 바꿔 부조를 드러내는 방법은 기각했습니다. int_plastic을
     * Object_46(삼각형 22,664) / 47(9,389) / 48(3,419)이 함께 써서 대시보드와
     * 도어트림까지 전부 반들거리게 됩니다.
     *
     * 그래서 같은 에셋의 배지 아틀라스에서 워드마크를 잘라 덧붙입니다.
     *
     * 크기를 폭에 맞춘 이유: 아틀라스 아트워크는 12:1인데 양각 형상은 8.2:1로
     * 비율이 다릅니다(서로 독립적으로 만들어진 것). 높이를 맞추면 폭이 78mm가
     * 되어 허브 폭(65mm)을 넘습니다. 그래서 폭 50mm를 맞추고 높이는 아트워크
     * 비율을 따릅니다. 양각 글자가 위아래로 1mm쯤 남지만 부조가 원래 거의
     * 보이지 않아 크게 드러나지 않습니다.
     *
     * 위치는 양각 글자 발자리 중심(y 0.9007 / z -0.3676)을 허브 평면
     * (법선 [-0.948, 0.319, 0.017], 기준점 [0.3749, 0.9008, -0.3750]) 위로 옮긴 뒤
     * 최대 돌출(0.94mm)보다 0.6mm 앞인 1.54mm를 띄운 좌표입니다.
     *
     * 법선이 수직에서 멀어 lookAt 기본 up(0,1,0)으로 롤이 안정적으로 정해집니다.
     */
    decals: [
      {
        id: 'steering-wordmark',
        image: 'teslaWordmark',
        position: [0.3735, 0.9013, -0.3676],
        normal: [-0.948, 0.319, 0.017],
        size: [0.0505, 0.00421],
        /*
         * 실차 허브 워드마크는 무광 은색입니다. 실내는 빛이 약해서 기본
         * envMapIntensity(1.4)를 그대로 두고, 금속기를 조금만 줍니다.
         */
        metalness: 0.3,
        roughness: 0.45,
      },
    ],

    credit: {
      title: '2024 Tesla Model 3 + Interior',
      author: 'tonielpro520',
      authorUrl: 'https://sketchfab.com/tonielpro520',
      modelUrl: 'https://sketchfab.com/3d-models/2024-tesla-model-3-interior-61ddcbdbd3854c239e0c95bf60b00022',
      licenseName: 'CC BY 4.0',
      licenseUrl: 'http://creativecommons.org/licenses/by/4.0/',
      allowsCommercialUse: true,
    },
  },

  {
    id: 'tesla-model-y',
    brand: 'Tesla',
    name: 'Tesla Model Y',
    category: '전기 SUV',
    powertrain: 'ev',
    shortName: 'Model Y',
    // 2020년 3월 첫 인도. 2025년 Juniper 부분변경도 같은 세대이고 아직 생산 중입니다.
    productionYears: { start: 2020 },
    basePrice: 60990000,
    // 2025 에셋에 세부 트림 표기가 없어 Long Range AWD 사양을 사용합니다.
    topSpeedKmh: 201,
    zeroTo100KmhSeconds: 4.8,
    // EV Specifications 2025 Model Y Long Range AWD 공차중량 1,994kg.
    kerbWeightKg: 1995,
    electricEnergy: {
      usableBatteryKWh: 75,
      initialSoc: 0.8,
      regenLimitKw: 85,
      driveEfficiency: 0.91,
      regenEfficiency: 0.76,
      referenceConsumptionWhPerKm: 175,
      accessoryPowerKw: 0.75,
    },

    asset: {
      url: '/models/modely.glb',
      dracoDecoderPath: DRACO_DECODER_PATH,
      // 전조등이 z=+3.272, 후미등이 z=-0.938입니다.
      sourceFrontAxis: [0, 0, 1],
      rotationY: FRONT_PLUS_Z_ROTATION,
      scale: 1,
      /**
       * 이 에셋만 원점에서 벗어나 있습니다(원본 중심 [1.766, 0.81, 1.173]).
       * position은 회전 뒤에 적용되므로 회전된 좌표계로 적어야 합니다.
       * 회전 후 중심이 [1.173, 0.81, -1.766]이고 최저점이 y=-0.015이므로
       * 그만큼 되돌려 원점에 세웁니다. 타이어 중심 y가 타이어 반지름과
       * 일치(0.369 ≈ 0.3685)하는 것으로 이 보정이 맞다는 것을 확인했습니다.
       */
      position: [-1.173, 0.015, 1.766],
    },

    // 실물 스펙 대비: 길이 4.751 / 높이 1.624 / 휠베이스 2.890
    bounds: {
      rearX: -2.394,
      frontX: 2.394,
      groundY: 0,
      roofY: 1.649,
      halfWidth: 1.062,
      frontWheelX: 1.484,
      rearWheelX: -1.418,
      wheelHalfTrack: 0.803,
      tireOuterRadius: 0.369,
    },

    /**
     * 실내 지오메트리가 없는 외관 전용 에셋입니다.
     * 삼각형 307,356개 중 82%가 타이어·휠이고, 실내 머티리얼이 하나도 없습니다.
     * null이면 UI가 "내부" 보기와 내장 색상 옵션을 자동으로 비활성화합니다.
     */
    interiorEye: null,

    /**
     * 이 에셋은 머티리얼 이름이 `black`, `black222`, `Material_2125767099`처럼
     * 의미가 없고 여러 부위가 공유합니다. 그래서 전부 메시 이름으로 지정하고,
     * 어느 메시가 어느 부위인지는 헤드리스 렌더로 픽셀을 재서 확인했습니다.
     *   Object_15 = 외부 차체 쉘 (전장 4.71 / 전폭 2.119)
     *   Object_13 = 림 (지름 0.584, y중심 0.353)
     *   Object_4 = 캐빈 글래스, Object_7 = 파노라마 루프
     *
     * 림 이름에 `_FL` 같은 꼬리가 붙은 이유: 원본은 림 네 개가 한 프리미티브였습니다.
     * 타이어(Object_16~19)는 모서리별로 나뉘어 있는데 림이 한 덩어리라, 주행 중에
     * 검은 타이어만 돌고 스포크가 있는 림은 서 있었습니다 — 화면에서는 바퀴가 아예
     * 돌지 않는 것으로 보입니다. 빌드 단계(scripts/split-wheels.mjs)에서 모서리별로
     * 갈랐고, 원본 노드는 껍데기로 남아서 이름으로 잡히지 않습니다.
     */
    selectors: {
      [MESH_ROLES.PAINT]: { meshes: ['Object_15'] },
      [MESH_ROLES.GLASS]: { meshes: ['Object_4', 'Object_7'] },
      [MESH_ROLES.RIM]: { meshes: ['Object_13_FL', 'Object_13_FR', 'Object_13_RL', 'Object_13_RR'] },
    },

    /**
     * 프런트 번호판.
     *
     * 이 에셋에는 번호판이 **형상으로 있습니다**. `Object_9`(머티리얼 `04_-_Default`,
     * 삼각형 128개)가 앞판과 뒤판 두 장이고, 각각을 검은 프레임 `Object_29`
     * (머티리얼 `black`)가 감싸고 있습니다. 정면 렌더로 모양을 확인했습니다
     * (probe-visible-surface.mjs --views=front --by=mesh --highlight=Object_9,Object_29).
     *
     * 왜 역할(selectors.plate)로 텍스처를 입히지 못하는가:
     *   `Object_9`에 **UV가 없습니다**. 프리미티브 속성이 POSITION과 NORMAL뿐입니다
     *   (프레임 `Object_29`도 같습니다). UV가 없으면 텍스처를 어디에 어떻게 붙일지
     *   정의되지 않아서, 머티리얼이 순수해도(둘 다 프리미티브 1개만 씀) 갈아끼우는
     *   방법을 쓸 수 없습니다. 그래서 Taycan처럼 자리 위에 판을 얹습니다.
     *
     *   참고로 이 차를 처음에 "번호판 없음"으로 본 이유는 `Object_9` 하나에 앞뒤 두
     *   장이 들어 있어서입니다. 프리미티브 바운딩 박스가 x -2.335~2.381로 차 길이만큼
     *   벌어져 판 모양으로 잡히지 않습니다(Model S의 번호판과 같은 구조).
     *
     * 앞판 실측 (Object_9에서 법선이 +X인 삼각형 64개, 정점 기준):
     *   범위      x 2.3660~2.3807 / y 0.4912~0.5916 / z -0.2464~0.2503
     *   크기      496.8 x 100.4 mm (비율 4.949)
     *   중심      [2.3733, 0.5414, 0.0019]
     *   평균 법선 [0.9999, 0.0163, 0]  (정면에서 위로 0.93도)
     *   일관성    평균 내적 0.9969 / 최악 0.9954 — 평면입니다
     *   프레임    Object_29가 513 x 114 mm로 판보다 크고, 가장 앞선 정점이 x 2.3820
     *
     *   z 중심이 0이 아니라 +0.0019인 것은 에셋이 그만큼 치우쳐 있어서입니다.
     *   0으로 맞추면 판이 프레임 안에서 2mm 밀립니다. 실측값을 그대로 씁니다.
     *
     * 크기는 판 면 실측값(497 x 100 mm)을 씁니다. 프레임 바깥 크기(513 x 114)로
     * 잡으면 검은 테두리를 덮어 버려서 번호판이 프레임에 끼워진 모습이 사라집니다.
     *
     * 띄우는 양:
     *   판과 프레임을 합쳐 가장 앞선 정점이 x 2.3820입니다. 판 중심을 2.385에 두면
     *   0.93도 기울어져도(위아래 ±0.8mm) 어디서나 최소 2.2mm 앞입니다.
     *   차량 앞끝(bounds.frontX 2.394)보다 안쪽이라 카메라 프레이밍에 영향이 없습니다.
     *
     * 재질은 다른 번호판들과 같은 값으로 맞춥니다(createPlateMaterials와 동일).
     */
    platePanel: {
      position: [2.385, 0.5414, 0.0019],
      normal: [0.9999, 0.0163, 0],
      size: [0.497, 0.1],
      metalness: 0.05,
      roughness: 0.45,
      envMapIntensity: 0.9,
    },

    credit: {
      title: '2025 Tesla Model Y',
      author: 'BloxBloger',
      authorUrl: 'https://sketchfab.com/BloxBloger',
      modelUrl: 'https://sketchfab.com/3d-models/2025-tesla-model-y-619601e7800d418da5922c4fa7833f74',
      licenseName: 'CC BY-NC 4.0',
      licenseUrl: 'http://creativecommons.org/licenses/by-nc/4.0/',
      // 비상업적 이용만 허용합니다. 이 프로젝트를 상업적으로 공개하려면
      // 이 모델을 빼거나 저작자에게 별도 허락을 받아야 합니다.
      allowsCommercialUse: false,
    },
  },

  {
    id: 'sorento-phev',
    brand: 'Kia',
    name: 'Kia Sorento',
    category: '플러그인 하이브리드 SUV',
    // 엔진 + 6단 자동변속기를 그대로 갖고 있어 계기판은 내연기관과 같습니다.
    powertrain: 'phev',
    shortName: 'Sorento',
    /*
     * 4세대(MQ4)만 적습니다. Sorento 명판은 2002년부터지만 우리 에셋은 4세대이고,
     * 세대가 셋 바뀌는 동안 같은 차였던 적이 없습니다. PHEV는 2020년 말부터
     * 화성공장에서 나왔지만 세대 기준으로 2020년을 시작으로 둡니다.
     */
    productionYears: { start: 2020 },
    basePrice: 48900000,
    // 4세대 Sorento PHEV 제조사 공개 성능.
    topSpeedKmh: 193,
    zeroTo100KmhSeconds: 8.7,
    // Edmunds Sorento Plug-In Hybrid 공차중량 4,652 lb (구동 배터리 포함 AWD).
    kerbWeightKg: 2110,

    asset: {
      url: '/models/sorento.glb',
      dracoDecoderPath: DRACO_DECODER_PATH,
      // 이 에셋만 전면이 -Z입니다. 위 FRONT_MINUS_Z_ROTATION 주석에 근거가 있습니다.
      sourceFrontAxis: [0, 0, -1],
      rotationY: FRONT_MINUS_Z_ROTATION,
      scale: 1,
      /**
       * 원본 최저점이 y=-0.085라 그만큼 들어 올려 바닥에 세웁니다.
       * 보정 후 타이어 중심 y(0.355)가 타이어 반지름(0.356)과 일치하는 것으로
       * 값이 맞다는 것을 확인했습니다.
       */
      position: [0, 0.085, 0],
    },

    // 실물 스펙 대비: 길이 4.810 / 높이 1.695 / 휠베이스 2.815 (에셋이 조금 작게 만들어져 있습니다)
    bounds: {
      rearX: -2.413,
      frontX: 2.284,
      groundY: 0,
      roofY: 1.659,
      // 사이드미러를 포함한 폭입니다. 차체만이면 0.95 정도입니다.
      halfWidth: 1.118,
      frontWheelX: 1.374,
      rearWheelX: -1.347,
      wheelHalfTrack: 0.87,
      tireOuterRadius: 0.356,
    },

    /**
     * 스티어링 휠 중심(앱 좌표 [0.44, 0.96, -0.375])에서 뒤로 0.41m, 위로 0.17m
     * 물러난 지점입니다. Taycan·Model 3과 같은 방식으로 잡았습니다.
     * SUV라 눈높이가 세단들(1.04~1.05)보다 높습니다.
     */
    interiorEye: [0.03, 1.13, 0.375 * DRIVER_SIDE_Z.left],
    driverSide: 'left',

    /**
     * 이 에셋은 Sketchfab을 거치며 머티리얼이 심하게 병합됐습니다. 원본 400여 종이
     * 37개로 합쳐졌는데, 합쳐진 기준이 의미와 무관합니다. 예를 들어
     * `template_fabrics__carpet_1` 하나가 원본 머티리얼 119종(플라스틱·고무·외장 트림)을
     * 덮고 있어서 그걸 칠하면 실내와 외장이 함께 물듭니다.
     *
     * 다행히 메시 이름에는 원본 머티리얼 이름이 남아 있습니다
     * (`PolySurface0.529_template_metalgraphite.002_0` 형태).
     * 그래서 병합돼도 순도가 유지된 머티리얼만 머티리얼로 고르고, 나머지는 메시로 고릅니다.
     * 순도는 scripts/inspect-model.mjs 로 머티리얼별 원본 구성을 세어서 확인했습니다.
     */
    selectors: {
      // 원본 머티리얼 1종(template_carpaint.014)만 들어 있어 그대로 쓸 수 있습니다.
      [MESH_ROLES.PAINT]: { materials: ['template_carpaint.014'] },
      // 네 머티리얼 모두 유리 원본만 담고 있습니다(순도 확인).
      [MESH_ROLES.GLASS]: {
        materials: [
          'template_glasstinted.003',
          'template_glassclear.010',
          'template_glassclear.011',
          'template_glassclear.008',
        ],
      },
      /**
       * 림은 메시 이름으로 고릅니다. 림이 속한 `template_fabrics__carpet_3`을
       * 크롬·블랙페인트·유리 등 75개 프리미티브가 함께 쓰기 때문에, 머티리얼로 고르면
       * 휠 옵션이 차체 절반을 칠해 버립니다.
       *
       * 삼각형 1,787개짜리 네 개가 림 디스크입니다. 같은 metalgraphite 계열의
       * 176개짜리들은 휠 볼트 같은 작은 부품이라 제외했습니다.
       */
      [MESH_ROLES.RIM]: {
        meshes: [
          'PolySurface0.529_template_metalgraphite.002_0',
          'PolySurface0.414_template_metalgraphite.011_0',
          'PolySurface0.425_template_metalgraphite.014_0',
          'PolySurface0.428_template_metalgraphite.015_0',
        ],
      },
      // 가죽 계열이 모인 세 머티리얼입니다. _5와 _4는 시트 원본만, _7은 18개 중 15개가 시트입니다.
      [MESH_ROLES.SEAT]: {
        materials: ['template_fabrics__carpet_7', 'template_fabrics__carpet_5', 'template_fabrics__carpet_4'],
      },
      /**
       * 트림은 헤드라이너(_10)와 바닥 카펫(_6)만 잡습니다. 둘 다 원본이 전부
       * 실내 부품이라 외장으로 새지 않습니다.
       *
       * 대시보드는 일부러 뺐습니다. 대시보드가 들어 있는 `template_fabrics__carpet_1`은
       * 차 전체를 덮는(길이 4.64m) 잡탕이라, 트림 색을 주면 범퍼 플라스틱까지 같이
       * 물듭니다. 대시보드까지 칠하려면 빌드 단계에서 메시를 갈라야 합니다
       * (718 Boxster의 scripts/split-interior.mjs 와 같은 방식).
       */
      [MESH_ROLES.TRIM]: {
        materials: ['template_fabrics__carpet_10', 'template_fabrics__carpet_6'],
      },
      /**
       * 앞·뒤 번호판. 이 에셋은 둘이 별개 메시라 앞뒤가 모두 바뀝니다.
       *
       * 뒤판은 머티리얼로 고를 수 있습니다. `template_plasticshiny__plates.001`이
       * 프리미티브 하나(`PolySurface0.217_...`)만 쓰기 때문입니다 — 이 에셋의 병합
       * 머티리얼 중 드물게 순도가 남은 쪽입니다.
       *
       * 앞판은 메시 이름으로 고릅니다. 런타임 머티리얼이 병합 잡탕 이름
       * (`template_fabrics__carpet_23`)으로 바뀌어 있어서입니다. 지금 그 머티리얼도
       * 프리미티브 하나만 쓰긴 하지만, 이름에 근거가 없어서 다시 익스포트하면 다른
       * 부품이 같은 버킷에 묶일 수 있습니다. 메시 이름에는 원본 머티리얼 이름
       * (`..._plasticshiny__plates_0`)이 남아 있어 의도가 이름에 드러납니다.
       *
       * 메시 이름 지정이 머티리얼 지정보다 우선하므로, 앞판이 다른 역할로
       * 새어 들어가는 일도 없습니다.
       */
      [MESH_ROLES.PLATE]: {
        materials: ['template_plasticshiny__plates.001'],
        meshes: ['PolySurface0.385_template_plasticshiny__plates_0'],
      },
    },

    /**
     * 번호판 가시면 실측: 뒤판 z 0.485 x y 0.107 m / 앞판 z 0.485 x y 0.107 m
     * (485 x 107 mm, 유럽식 긴 판). 두 판의 비율이 4.557과 4.546으로 거의 같아
     * 텍스처 하나로 둘 다 맞습니다(차이 0.2%).
     *
     * 법선으로 걸러 각각 삼각형 80개를 재서 얻은 값입니다.
     *
     * ⚠ 이 판의 UV는 캔버스를 다 쓰지 않습니다: u 0.017~0.980 / v 0.113~0.945.
     * 세로로 83.2%만 나타나서, 카탈로그에서 로고 크기 상한(licensePlate.ts의
     * LOGO_MAX_FILL)을 정하는 것이 이 판입니다. 여유 없이 키우면 여기서만
     * "towncar" 글자 아래가 판 밖으로 잘립니다. 그쪽 주석에 계산이 있습니다.
     */
    plate: { aspect: 4.55 },
  },

  {
    id: 'model-s-plaid',
    brand: 'Tesla',
    name: 'Tesla Model S',
    category: '전기 스포츠 세단',
    powertrain: 'ev',
    shortName: 'Model S',
    /*
     * 카탈로그에서 유일하게 끝이 확정된 차입니다. 2012년 6월 첫 인도, 2026년 봄
     * Fremont 공장이 Optimus 생산으로 전환하면서 Model X와 함께 단산했습니다.
     * 14년간 부분변경만 있었고 세대 교체는 없었습니다.
     */
    productionYears: { start: 2012, end: 2026 },
    basePrice: 159900000,
    // 원본 에셋의 Plaid + Track Package 사양(Drag Strip Mode 기준 0→100 2.1초).
    topSpeedKmh: 322,
    zeroTo100KmhSeconds: 2.1,
    // J.D. Power Model S Plaid 공차중량 4,766 lb.
    kerbWeightKg: 2165,
    electricEnergy: {
      usableBatteryKWh: 95,
      initialSoc: 0.8,
      regenLimitKw: 250,
      driveEfficiency: 0.9,
      regenEfficiency: 0.77,
      referenceConsumptionWhPerKm: 195,
      accessoryPowerKw: 0.85,
    },

    asset: {
      url: '/models/model-s.glb',
      dracoDecoderPath: DRACO_DECODER_PATH,
      /*
       * 전조등(vehiclelights128__FR)과 방향지시등이 z 최대단, 후미등(RedMain)과
       * 리플렉터가 최소단에 있습니다. 페달이 계기판보다 앞쪽인 것으로 교차 확인했습니다.
       */
      sourceFrontAxis: [0, 0, 1],
      rotationY: FRONT_PLUS_Z_ROTATION,
      /*
       * 이 에셋만 단위가 센티미터입니다. 원본 전장이 503.977이고 0.01배 하면 5.04m로
       * 실물 Model S(4.97m)와 맞습니다. 폭 2.21m(미러 포함), 높이 1.49m도 일치합니다.
       */
      scale: 0.01,
      /**
       * 원본이 원점에서 크게 벗어나 있습니다(회전·스케일 후 중심 [-6.412, _, 1.028]).
       * position은 회전과 스케일 뒤에 적용되므로 그 좌표계로 적습니다.
       * 최저점이 이미 y=0이라 높이 보정은 필요 없고, 타이어 중심 y(0.36)가 타이어
       * 반지름(0.36)과 일치하는 것으로 접지가 맞다는 것을 확인했습니다.
       */
      position: [6.412, 0, -1.028],
    },

    // 실물 스펙 대비: 길이 4.970 / 높이 1.445 / 휠베이스 2.960
    bounds: {
      rearX: -2.52,
      frontX: 2.52,
      groundY: 0,
      roofY: 1.49,
      // 사이드미러를 포함한 폭입니다.
      halfWidth: 1.104,
      frontWheelX: 1.594,
      rearWheelX: -1.462,
      wheelHalfTrack: 0.86,
      tireOuterRadius: 0.36,
    },

    /**
     * 스티어링 요크 중심(앱 좌표 [0.41, 0.943, -0.37])에서 뒤로 0.41m, 위로 0.152m
     * 물러난 지점입니다. Model 3과 같은 값을 썼습니다.
     *
     * 요크 위치가 Model 3(0.39, 0.898, -0.375)과 Taycan(0.396, 0.868, -0.37)과
     * 거의 같게 나온 것이, cm 단위 스케일과 원점 보정이 맞다는 교차 확인입니다.
     */
    interiorEye: [0, 1.095, 0.37 * DRIVER_SIDE_Z.left],
    driverSide: 'left',

    /**
     * 카탈로그에서 머티리얼 이름이 가장 친절합니다. 부위별로 이름이 그대로 달려 있어서
     * (Pearl_White_Multi_Coat / Windows / Rims / Interior) 전부 머티리얼로 고를 수
     * 있습니다. 메시 이름은 Object_NN 자동 생성이라 쓸 수 없습니다.
     *
     * 림을 머티리얼로 고를 수 있는 이유: `Rims`의 AABB가 y 0.07~0.65 구간에만
     * 있습니다(휠 높이). 차체와 공유하지 않는다는 뜻입니다. 다른 에셋들은 림
     * 머티리얼을 차체가 함께 써서 메시 이름으로 좁혀야 했습니다.
     */
    selectors: {
      [MESH_ROLES.PAINT]: { materials: ['M_2022_Tesla_Model_S_Plaid_Pearl_White_Multi_Coat'] },
      [MESH_ROLES.GLASS]: { materials: ['M_2022_Tesla_Model_S_Plaid_Windows'] },
      [MESH_ROLES.RIM]: { materials: ['M_2022_Tesla_Model_S_Plaid_Rims'] },
      // koja(가죽) + alc(알칸타라). 둘 다 캐빈 범위 안에만 있습니다.
      [MESH_ROLES.SEAT]: {
        materials: ['M_2022_Tesla_Model_S_Plaid_koja_3__spec', 'M_2022_Tesla_Model_S_Plaid_alc__spec'],
      },
      [MESH_ROLES.TRIM]: {
        materials: [
          'M_2022_Tesla_Model_S_Plaid_Interior',
          'M_2022_Tesla_Model_S_Plaid_common_carpet_laurp__spec',
        ],
      },
      /**
       * Model 3에 이어 두 번째로 화면이 독립 머티리얼로 있는 에셋입니다.
       * 센터 17인치(midfntscrn)와 뒷좌석 8인치(rearmidscrn)가 별개 머티리얼입니다.
       * 그래서 Taycan처럼 판을 얹지 않고 원본 면에 텍스처를 입힙니다.
       */
      [MESH_ROLES.SCREEN]: {
        materials: [
          'M_2022_Tesla_Model_S_Plaid_midfntscrn__spec',
          'M_2022_Tesla_Model_S_Plaid_rearmidscrn__spec',
        ],
      },
      /**
       * 앞·뒤 번호판. 카탈로그에서 유일하게 앞뒤가 다 있는 차입니다.
       *
       * 머티리얼 이름만 `M_2022_Tesla_Model_S_Plaid_` 접두사가 없습니다
       * (`Philippine_License_Plate_Recovered`). 다른 곳에서 가져다 붙인 판이라는
       * 뜻이고, 이름 그대로 필리핀 번호판입니다. 쇼룸 차가 달고 있을 이유가 없어서
       * 갈아끼웁니다.
       *
       * 앞뒤를 따로 지정할 수는 없습니다. 프리미티브 하나(`Object_23`, 노드
       * `Object_52`)에 정점 8개 = 사각형 둘로 앞판과 뒤판이 함께 들어 있습니다.
       * 두 판의 크기가 같아서(각 0.324 x 0.113 m) 텍스처 하나로 둘 다 맞습니다.
       */
      [MESH_ROLES.PLATE]: { materials: ['Philippine_License_Plate_Recovered'] },
    },

    /**
     * 두 화면의 실측 비율이 센터 1.576 / 뒷좌석 1.756입니다. 머티리얼이 달라서
     * 따로 줄 수도 있지만, 화면 텍스처는 모델당 하나만 굽기 때문에 중간값인
     * 16:10을 씁니다(Model 3과 같은 이유).
     */
    display: {
      aspect: 16 / 10,
      backgroundColor: '#0a0d14',
    },

    /**
     * 번호판 가시면 실측: 각 판이 z 0.324 x y 0.113 m (324 x 113 mm).
     *
     * 앞뒤가 사각형 하나씩이라 테두리가 없고, UV가 정확히 0~1이라 잴 것이
     * 많지 않았습니다. 뒤판 중심 [-2.423, 0.797, 0] / 앞판 중심 [2.498, 0.413, 0].
     *
     * 두 판의 u 방향이 서로 반대입니다(뒤판은 +z, 앞판은 -z). 그게 정방향입니다 —
     * 차 뒤에 선 사람의 오른쪽이 +z, 차 앞에 선 사람의 오른쪽이 -z이므로 둘 다
     * 관찰자 기준으로는 왼쪽에서 오른쪽으로 흐릅니다. 그래서 좌우 반전 보정 없이
     * 같은 텍스처를 그대로 씁니다.
     */
    plate: { aspect: 2.88 },
  },

  {
    id: 'm8-f92',
    brand: 'BMW',
    name: 'BMW M8',
    category: '고성능 쿠페',
    powertrain: 'ice',
    shortName: 'M8',
    /*
     * F92(쿠페)만 적습니다. M8 명판은 2019년 6월 컨버터블(F91)·쿠페(F92)로 시작했고
     * 그란쿠페(F93)가 10월에 붙었는데, 우리 에셋은 쿠페입니다. 쿠페는 2025년 초에
     * 단산했고(그란쿠페·컨버터블은 더 남았습니다) 8시리즈 전체도 2026년에 끝났습니다.
     * 명판 전체를 적으면 화면의 차와 무관한 구간이 됩니다.
     */
    productionYears: { start: 2019, end: 2025 },
    basePrice: 217700000,
    // 원본 에셋의 Competition + M Driver's Package 사양.
    topSpeedKmh: 305,
    zeroTo100KmhSeconds: 3.2,
    // Encycarpedia M8 Competition Coupé(F92) 공차중량 1,885kg.
    kerbWeightKg: 1885,

    asset: {
      url: '/models/m8.glb',
      dracoDecoderPath: DRACO_DECODER_PATH,
      /*
       * 원본 전면은 +Z입니다. 로우빔(z=+1.804)·하이빔(z=+1.929)이 최대단,
       * 테일램프 적색 유리(`m8f92_glass_red` z=-2.265)가 최소단입니다.
       * 계기판(z=+0.345)이 시트(z=-0.34)보다 +Z 쪽인 것으로 교차 확인했습니다.
       */
      sourceFrontAxis: [0, 0, 1],
      rotationY: FRONT_PLUS_Z_ROTATION,
      scale: 1,
      /**
       * 원본 최저점이 y=+0.001입니다. 1mm 떠 있어서 그만큼 내립니다.
       * 보정 후 앞타이어 중심 y(0.351)가 타이어 반지름(0.3515)과 일치합니다.
       */
      position: [0, -0.001, 0],
    },

    // 실물 스펙 대비: 길이 4.867 / 높이 1.365 / 휠베이스 2.827 (거의 그대로입니다)
    bounds: {
      rearX: -2.668,
      frontX: 2.246,
      groundY: 0,
      roofY: 1.38,
      // 사이드미러를 포함한 폭입니다. 차체만이면 0.97 정도입니다.
      halfWidth: 1.079,
      frontWheelX: 1.284,
      rearWheelX: -1.55,
      wheelHalfTrack: 0.803,
      tireOuterRadius: 0.353,
    },

    /**
     * 스티어링 휠(`m8f92_steer.002...`, 원본 중심 [0.397, 0.873, 0.088] ->
     * 앱 [0.088, 0.872, -0.397])에서 뒤로 0.41m, 위로 0.17m 물러난 지점입니다.
     * 쿠페라 눈높이가 카탈로그에서 가장 낮은 편입니다(718 Boxster 0.95 다음).
     */
    interiorEye: [-0.322, 1.042, 0.397 * DRIVER_SIDE_Z.left],
    driverSide: 'left',

    /**
     * 이 에셋은 Forza 계열 명명 규칙을 씁니다. 머티리얼에 `m8f92_` 접두사가 붙고
     * 부위 이름이 그대로 달려 있어서(CarPaint / Window / Interior / Rim2A) 읽기 쉽습니다.
     * 메시 이름도 살아 있습니다(`m8f92_hood.001` · `m8f92_seat_FL.004` · `m8f92_doorpanel_L.003`).
     * 카탈로그에서 메시와 머티리얼이 둘 다 의미를 갖는 유일한 에셋입니다.
     *
     * 텍스처가 있지만(16장) 실내 색은 텍스처가 아니라 머티리얼에 있고,
     * scripts/probe-visible-surface.mjs 로 외장 점유율을 재서 이름과 대조했습니다.
     *   CarPaint 52.5% · Carbon1 10.1% · glass 9.4% · Coloured 9.0% · Tire1A 4.0%
     */
    selectors: {
      /**
       * `m8f92_CarPaint`가 외판(보닛·도어·범퍼·트렁크·펜더)이고
       * `m8f92_CarPaint2`는 차체색으로 칠하는 트림(루프 트림·도어 트림·미러 트림)입니다.
       * 실차도 이 부품들은 차체색이라 함께 칠하는 것이 맞습니다.
       *
       * `m8f92_Coloured`는 넣지 않았습니다. 이름은 도장처럼 보이지만 프리미티브 54개가
       * 휠 디스크·그릴·와이드 펜더·실내 대시까지 걸쳐 있어서, 칠하면 그릴과 휠이
       * 함께 물듭니다. 휠은 아래 RIM에서 메시 이름으로 따로 집습니다.
       */
      [MESH_ROLES.PAINT]: { materials: ['m8f92_CarPaint', 'm8f92_CarPaint2'] },
      /**
       * 유리가 세 머티리얼로 갈려 있습니다. 렌더로 확인한 담당 구역:
       *   `Window`      측면 유리 + 앞유리 상단 (삼각형 218개지만 면적은 가장 큼)
       *   `WindowDark`  리어 윈도우와 짙게 착색된 부분
       *   `m8f92_glass` 앞유리와 램프 렌즈·미러 거울 (프리미티브 28개)
       *
       * `m8f92_glass`를 넣으면 램프 렌즈까지 투명해집니다. 그래도 넣는 이유는
       * 여기에 앞유리가 들어 있어서입니다. 빼면 운전석에서 앞이 막힙니다.
       * 램프는 원래 투명 부품이라 부작용이 눈에 띄지 않습니다.
       *
       * 적색 테일램프(`m8f92_glass_red`)는 뺐습니다. 유리 틴트 색으로 덮으면
       * 후미등이 차체색 유리로 보입니다.
       */
      [MESH_ROLES.GLASS]: { materials: ['Window', 'WindowDark', 'm8f92_glass'] },
      /**
       * 림 디스크(삼각형 6,269개짜리 네 개)는 `m8f92_Coloured`를 그릴·펜더와 공유하므로
       * 메시 이름으로 집습니다. 림 바깥 립은 `m8f92_Rim2A` 전용이라 머티리얼로 집습니다
       * (프리미티브 4개가 모두 휠 중심에 있는 것을 확인했습니다).
       */
      [MESH_ROLES.RIM]: {
        meshes: [
          'm8f92_Wheel_04_m8f92_Coloured_0',
          'm8f92_Wheel_04.001_m8f92_Coloured_0',
          'm8f92_Wheel_04.002_m8f92_Coloured_0',
          'm8f92_Wheel_04.003_m8f92_Coloured_0',
        ],
        materials: ['m8f92_Rim2A'],
      },
      /**
       * 시트와 트림을 머티리얼로 가를 수 없습니다. 실내 머티리얼 다섯 개
       * (`m8f92_Interior` · `InteriorA` · `InteriorColor2` · `InteriorColourZone` ·
       * `InteriorTillingColourZone`)를 시트·대시보드·도어패널이 모두 공유합니다.
       * 예를 들어 `m8f92_Interior` 프리미티브 14개에는 시트 2개와 대시보드·캐빈 셸·
       * 스티어링 휠·페달이 함께 들어 있습니다.
       *
       * 다행히 메시 이름이 부위를 말해 줍니다. 그래서 `m8f92_seat_*` 열 개를 시트로,
       * `m8f92_dash*` · `m8f92_doorpanel_*` · `m8f92_body.0*`(캐빈 셸) · 선바이저를
       * 트림으로 나눕니다. 718 Boxster처럼 지오메트리를 가르지 않아도 됩니다.
       *
       * 시트 열 개는 한 시트가 재질별로 다섯 조각(가죽 본체·색상 존·타일링 존·
       * 스티치)으로 나뉘어 있고 좌우가 있기 때문입니다. 일부만 적으면 시트 한 장에
       * 두 색이 섞입니다.
       */
      [MESH_ROLES.SEAT]: {
        meshes: [
          'm8f92_seat_FL.004_m8f92_Interior_0',
          'm8f92_seat_FR.004_m8f92_Interior_0',
          'm8f92_seat_FL.001_m8f92_InteriorColor2_0',
          'm8f92_seat_FR.001_m8f92_InteriorColor2_0',
          'm8f92_seat_FL.003_m8f92_InteriorColourZone_0',
          'm8f92_seat_FR.003_m8f92_InteriorColourZone_0',
          'm8f92_seat_FL.002_m8f92_InteriorA_0',
          'm8f92_seat_FR.002_m8f92_InteriorA_0',
          'm8f92_seat_FL_m8f92_InteriorTillingColourZone_0',
          'm8f92_seat_FR_m8f92_InteriorTillingColourZone_0',
        ],
      },
      [MESH_ROLES.TRIM]: {
        meshes: [
          // 캐빈 셸 (삼각형 10,713개, 실내에서 가장 큰 면)
          'm8f92_body.008_m8f92_Interior_0',
          'm8f92_body.011_m8f92_InteriorColor2_0',
          'm8f92_body.012_m8f92_InteriorA_0',
          // 대시보드
          'm8f92_dash.005_m8f92_Interior_0',
          'm8f92_dash.001_m8f92_InteriorColor2_0',
          'm8f92_dash.006_m8f92_InteriorA_0',
          // 스티어링 휠 림
          'm8f92_steer.002_m8f92_Interior_0',
          // 도어 패널 좌우
          'm8f92_doorpanel_L.003_m8f92_Interior_0',
          'm8f92_doorpanel_R.003_m8f92_Interior_0',
          'm8f92_doorpanel_L.005_m8f92_InteriorColor2_0',
          'm8f92_doorpanel_R.005_m8f92_InteriorColor2_0',
          // 선바이저
          'm8f92_sunvisor_m8f92_Interior_0',
        ],
      },
      /**
       * 카탈로그에서 화면이 독립 머티리얼로 있는 세 번째 에셋입니다
       * (Model 3, Model S에 이어). 게다가 계기판과 센터 화면이 따로 있습니다.
       *   `m8f92_gauges_screen` 계기판  0.293 x 0.103 m (2.84:1), 중심 z -0.396(운전석)
       *   `m8f92_screen`       센터 화면 0.348 x 0.113 m (3.08:1), 중심 z -0.015
       */
      [MESH_ROLES.SCREEN]: { materials: ['m8f92_gauges_screen', 'm8f92_screen'] },
    },

    /**
     * 화면 텍스처는 모델당 하나만 굽기 때문에 비율을 하나만 고를 수 있습니다.
     * 두 화면의 실측이 2.84와 3.08이라 그 사이인 2.9를 씁니다. 어느 쪽도 눈에
     * 띄게 늘어나지 않고, carModels.test.ts가 요구하는 범위(1~3) 안에 들어옵니다.
     */
    display: {
      aspect: 2.9,
      backgroundColor: '#0a0d14',
    },

    /**
     * 이 에셋에는 **바디킷 세 종이 겹쳐 들어 있습니다.** 순정(`m8f92_*` 388개) 위에
     * Keyvany(33개)와 Manhart(6개), 와이드바디 로고(1개)가 같은 자리에 쌓여 있습니다.
     * 리어 범퍼는 세 장이 좌표까지 완전히 같습니다
     * (`m8f92_bumper_R.001` / `keyvany_bumper_R.001` / `manhart_bumper_R.001`
     *  전부 중심 [0, 0.516, -2.211] 크기 [1.892, 0.61, 0.837]).
     * 그대로 두면 리어 범퍼·디퓨저·머플러에서 z-fighting이 일어납니다.
     *
     * 그래서 순정만 남기고 나머지를 감춥니다. 함께 감추는 것 둘 더:
     *   `m5f90_gauges_m5f90_gauges_0`          M5 F90에서 넘어온 삼각형 6개짜리 잔재
     *   `#m8f92_gauges_...gauges_screen_0`      계기판 화면의 사본 (2mm 앞에 겹침)
     * 이름 앞의 `#`은 원본 도구에서 "끈 레이어"를 뜻하는 표시로 보입니다.
     *
     * 목록이 긴 이유는 바디킷 부품이 재질별로 쪼개져 있기 때문입니다(범퍼 하나가
     * CarPaint·Light·Carbon1·Coloured 네 조각). assetIntegrity.test.ts가 이름이
     * 실제로 존재하는지 하나씩 검사하므로, 오타는 테스트에서 걸립니다.
     */
    hidden: {
      meshes: [
        '#m8f92_gauges_m8f92_gauges_screen_0',
        'm5f90_gauges_m5f90_gauges_0',
        'wb_logo_F_blk_m8f92_Coloured_0',
        // Keyvany 바디킷
        'keyvany_bumper_R.001_m8f92_CarPaint_0',
        'keyvany_bumper_R.002_m8f92_Light_0',
        'keyvany_bumper_R.003_m8f92_Carbon1_0',
        'keyvany_bumper_R_m8f92_Coloured_0',
        'keyvany_diffuser_R_c.001_m8f92_Coloured_0',
        'keyvany_diffuser_R_c.002_m8f92_Light_0',
        'keyvany_diffuser_R_c.003_m8f92_taillight_0',
        'keyvany_diffuser_R_c.004_m8f92_Carbon1_0',
        'keyvany_diffuser_R_c.005_m8f92_SpecularTintA_0',
        'keyvany_diffuser_R_c_m8f92_glass_0',
        'keyvany_diffuser_extra_c_m8f92_SpecularTintA_0',
        'keyvany_fender_L.001_m8f92_CarPaint_0',
        'keyvany_fender_L.002_m8f92_Carbon1_0',
        'keyvany_fender_L.003_m8f92_Coloured_0',
        'keyvany_fender_L_m8f92_Badge_0',
        'keyvany_fender_R.001_m8f92_CarPaint_0',
        'keyvany_fender_R.002_m8f92_Carbon1_0',
        'keyvany_fender_R.003_m8f92_Coloured_0',
        'keyvany_fender_R_m8f92_Badge_0',
        'keyvany_m8 logo_m8f92_CarPaint_0',
        'keyvany_muffler_L2.001_m8f92_Coloured_0',
        'keyvany_muffler_L2_m8f92_Base_0',
        'keyvany_muffler_L_cf.001_m8f92_Coloured_0',
        'keyvany_muffler_L_cf.002_m8f92_Carbon1_0',
        'keyvany_muffler_L_cf_m8f92_Base_0',
        'keyvany_muffler_L_m8f92_Base_0',
        'keyvany_muffler_R.001_m8f92_Coloured_0',
        'keyvany_muffler_R2.001_m8f92_Coloured_0',
        'keyvany_muffler_R2_m8f92_Base_0',
        'keyvany_muffler_R_cf.001_m8f92_Coloured_0',
        'keyvany_muffler_R_cf.002_m8f92_Carbon1_0',
        'keyvany_muffler_R_cf_m8f92_Base_0',
        'keyvany_muffler_R_m8f92_Base_0',
        // Manhart 바디킷
        'manhart_bumper_R.001_m8f92_CarPaint_0',
        'manhart_bumper_R.002_m8f92_Light_0',
        'manhart_bumper_R_m8f92_SpecularTintA_0',
        'manhart_diffuser_extra_a_m8f92_SpecularTintA_0',
        'manhart_diffuser_extra_b.001_m8f92_Carbon1_0',
        'manhart_diffuser_extra_b_m8f92_SpecularTintA_0',
      ],
    },

    credit: {
      title: 'BMW M8 F92 Coupé Competition',
      author: 'kevin (ケビン)',
      authorUrl: 'https://sketchfab.com/sohyalebret',
      modelUrl: 'https://sketchfab.com/3d-models/bmw-m8-f92-coupe-competition-25d5b4f6d13e4217afa09bbf89f8d993',
      licenseName: 'CC BY 4.0',
      licenseUrl: 'http://creativecommons.org/licenses/by/4.0/',
      allowsCommercialUse: true,
    },
  },

];

export const DEFAULT_CAR_MODEL_ID = CAR_MODELS[0].id;

export function findCarModelById(modelId: string): CarModel | undefined {
  return CAR_MODELS.find((model) => model.id === modelId);
}

/**
 * 실내 눈높이가 정의된 모델만 "내부" 보기를 제공할 수 있습니다.
 *
 * 타입 가드로 선언한 이유는, 이 검사를 통과한 뒤에는 interiorEye가 확실히
 * 좌표라는 사실을 호출한 쪽에서도 알아야 하기 때문입니다. 그러지 않으면
 * 실내 카메라를 만드는 자리마다 null 검사를 다시 하게 됩니다.
 */
export function hasInteriorView(model: CarModel): model is CarModelWithInterior {
  return Array.isArray(model.interiorEye);
}
